import {Component, Input, OnInit} from '@angular/core';
import {MatCard, MatCardContent, MatCardTitle} from '@angular/material/card';
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {MatError, MatFormField} from '@angular/material/form-field';
import {MatInput} from '@angular/material/input';
import {MatButton} from '@angular/material/button';
import {NgIf} from '@angular/common';
import {MatLabel} from '@angular/material/select';
import {AdminService} from '../../../../service/admin.service';
import {ActivatedRoute, Router} from '@angular/router';
import {MatSlideToggle} from '@angular/material/slide-toggle';
import {AdminEvent} from '../../../../event/admin.event';
import {AdminEventTypeEnum} from '../../../../enum/admin-event-type.enum';
import {MatSnackBar} from '@angular/material/snack-bar';

/**
 * @description This component is used to display the sign-in form for the admin.
 * @class SignInAdminPageComponent
 * @export
 * @name SignInAdminPageComponent
 */
@Component({
  imports: [
    MatButton,
    MatCard,
    MatCardContent,
    MatCardTitle,
    MatError,
    MatFormField,
    MatInput,
    MatLabel,
    NgIf,
    ReactiveFormsModule,
    MatSlideToggle
  ],
  selector: 'app-page-admin-sign-in',
  standalone: true,
  styleUrl: './sign-in.component.scss',
  templateUrl: './sign-in.component.html'
})
export class SignInAdminPageComponent implements OnInit {

  /**
   * @description This property contains the sign-in form.
   * @memberof SignInAdminPageComponent
   * @name SignInAdminPageComponent#form
   * @property
   * @protected
   * @readonly
   * @type {FormGroup}
   */
  protected readonly form: FormGroup = new FormGroup({

    // Define the form controls for the email address field.
    emailAddress: new FormControl('', {

      // Define the validators for the email address field.
      validators: [Validators.email, Validators.required]
    }),

    // Define the form controls for the password field.
    password: new FormControl('', {

      // Define the validators for the password field.
      validators: [Validators.required]
    }),

    // Define the form controls for the remember me field.
    persistent: new FormControl(false, {

      // The default value should never be null.
      nonNullable: true
    })
  });

  /**
   * @description This property contains the error message.
   * @memberof SignInAdminPageComponent
   * @name SignInAdminPageComponent#errorMessage
   * @property
   * @public
   * @type {string | null}
   */
  @Input() public errorMessage: string | null = null;

  /**
   * @description This property contains the optional URL to redirect the admin to after signing in.
   * @memberof SignInAdminPageComponent
   * @name SignInAdminPageComponent#returnUrl
   * @property
   * @public
   * @type {string}
   */
  @Input() public returnUrl: string = '/admin/dashboard';

  /**
   * @constructor
   * @description This method instantiates a new instance of the SignInAdminPageComponent class.
   * @memberof SignInAdminPageComponent
   * @name SignInAdminPageComponent#constructor
   * @param {AdminService} adminService The injected instance of our admin service provider.
   * @param {ActivatedRoute} route The injected instance of the activated route.
   * @param {Router} router The injected instance of our router.
   * @param {MatSnackBar} snackbar The injected instance of our snackbar.
   * @public
   */
  public constructor(
    /**
     * @description This property contains the injected instance of our admin service provider.
     * @memberof SignInAdminPageComponent
     * @name SignInAdminPageComponent#adminService
     * @private
     * @property
     * @readonly
     * @type {AdminService}
     */
    private readonly adminService: AdminService,
    /**
     * @description This property contains the injected instance of the activated route.
     * @memberof SignInAdminPageComponent
     * @name SignInAdminPageComponent#router
     * @private
     * @property
     * @readonly
     * @type {ActivatedRoute}
     */
    private readonly route: ActivatedRoute,
    /**
     * @description This property contains the injected instance of our router.
     * @memberof SignInAdminPageComponent
     * @name SignInAdminPageComponent#router
     * @private
     * @property
     * @readonly
     * @type {Router}
     */
    private readonly router: Router,
    /**
     * @description This property contains the injected instance of our snackbar.
     * @memberof SignInAdminPageComponent
     * @name SignInAdminPageComponent#snackbar
     * @private
     * @property
     * @readonly
     * @type {MatSnackBar}
     */
    private readonly snackbar: MatSnackBar
  ) {
  }

  /**
   * @abstract
   * @description This method is called when the component is initialized.
   * @memberof SignInAdminPageComponent
   * @name SignInAdminPageComponent#ngOnInit
   * @method
   * @public
   * @returns {void}
   */
  public ngOnInit(): void {

    // Subscribe to the query parameters of the activated route.
    this.route.queryParams.subscribe((params) => {

      // Check if the return URL is defined in the query parameters the set the return URL property into the component instance.
      if (!!params['returnUrl']) this.returnUrl = params['returnUrl'];

      // Check for an already online session then redirect to the dashboard.
      if (this.adminService.online()) this.router.navigate([this.returnUrl]).then((): void => {});

      // Bind to the session start or session continued event.
      this.adminService.events.subscribe((e: AdminEvent): void => {

        // Check if the event is a session start or session continued event then redirect to the dashboard.
        if (e.type === AdminEventTypeEnum.SessionStart || e.type === AdminEventTypeEnum.SessionContinued)
          this.router.navigate([this.returnUrl]).then((): void => {
          });
      });
    });
  }

  /**
   * @description This method is called when the form is submitted.
   * @memberof SignInAdminPageComponent
   * @name SignInAdminPageComponent#submit
   * @method
   * @public
   * @returns {void}
   */
  public submit(): void {

    // Ensure the form is valid, then try to sign the admin into the system.
    if (this.form.valid) this.adminService.signIn(this.form.controls['emailAddress'].value.trim(),
      this.form.controls['password'].value.trim(), this.form.controls['persistent'].value).subscribe({

      // Handle the completion of the sign-in process.
      complete: () => this.router.navigate([this.returnUrl]).then((): void => {
      }),

      // Catch any errors that occur during the sign-in process.
      error: (e: Error) => this.snackbar.open(`Sign-In failed with:  ${e.message}`, 'Dismiss', {
        duration: 50000,
        horizontalPosition: 'end',
        panelClass: ['snackbar-error'],
        verticalPosition: 'bottom'
      })
    });
  }
}
